import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function PayCharges() {
  const { bookingId } = useParams();
  const [email, setEmail] = useState("");
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = async () => {
    if (!email.trim()) return toast.error("Enter the email used on your booking");
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/${bookingId}`, { params: { email: email.trim() } });
      setB(data);
      setError("");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/pay/charges`, {
        origin_url: window.location.origin,
        email: email.trim(),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setPaying(false);
    }
  };

  const lines = b
    ? [
        Number(b.pi_toll_amount) > 0 && ["Paradise Island bridge toll", b.pi_toll_amount],
        Number(b.smoking_fee) > 0 && ["Smoking penalty", b.smoking_fee],
        Number(b.cleaning_fee) > 0 && ["Cleaning fee", b.cleaning_fee],
        Number(b.fuel_charge) > 0 && [`Fuel refill — ${b.fuel_gallons} gal @ $8.00/gal`, b.fuel_charge],
        Number(b.late_fee) > 0 && [`Late return — ${b.late_hours} hr @ $25/hr`, b.late_fee],
        Number(b.cancellation_fee) > 0 && ["Cancellation fee (20% of rental cost)", b.cancellation_fee],
      ].filter(Boolean)
    : [];
  const chargesTotal = lines.reduce((s, [, a]) => s + Number(a), 0);
  const paidAlready = Number(b?.charges_paid_amount) || 0;
  const due = Math.max(0, Math.round((chargesTotal - paidAlready) * 100) / 100);
  const fee = Math.round(due * 4.5) / 100;
  const vat = Math.round(due * 10) / 100;

  return (
    <div data-testid="pay-charges-page" className="max-w-md mx-auto px-6 pt-40 pb-24">
      <img src="/logo.png" alt="Easy Drive Car Rental" data-testid="pay-charges-logo" className="h-14 w-auto object-contain mb-4" />
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Car Rental</p>
      <h1 className="mt-3 font-display text-4xl tracking-tight">Post-rental <span className="italic gold-text">charges</span></h1>

      {!b && (
        <div className="mt-8 space-y-4">
          <p className="text-sm text-muted-foreground">Enter the email used on your booking to view and settle outstanding charges.</p>
          {error && <p data-testid="pay-charges-error" className="text-sm text-destructive font-ui">{error}</p>}
          <div className="flex gap-3">
            <Input data-testid="pay-charges-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="input-dark" onKeyDown={(e) => e.key === "Enter" && load()} />
            <Button data-testid="pay-charges-load" onClick={load} disabled={loading} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shrink-0">
              {loading ? <Loader2 size={15} className="animate-spin" /> : "View"}
            </Button>
          </div>
        </div>
      )}

      {b && (
        <div className="mt-8 bg-card border border-border rounded-2xl p-6 space-y-3">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Booking {b.booking_ref} — {b.vehicle_name}</p>
          {lines.map(([label, amt]) => (
            <div key={label} className="flex items-center justify-between text-sm font-ui">
              <span className="text-muted-foreground">{label}</span>
              <span>{fmtMoney(amt)}</span>
            </div>
          ))}
          {paidAlready > 0 && (
            <div className="flex items-center justify-between text-sm font-ui text-emerald-600">
              <span>Already paid</span>
              <span>−{fmtMoney(paidAlready)}</span>
            </div>
          )}
          {due > 0 ? (
            <>
              <div className="flex items-center justify-between text-sm font-ui">
                <span className="text-muted-foreground">VAT (10%)</span>
                <span>{fmtMoney(vat)}</span>
              </div>
              <div className="flex items-center justify-between text-sm font-ui">
                <span className="text-muted-foreground">Processing fee (5%)</span>
                <span>{fmtMoney(fee)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="font-ui font-semibold">Total due</span>
                <span data-testid="pay-charges-total" className="gold-text font-ui text-2xl font-semibold">{fmtMoney(due + fee + vat)}</span>
              </div>
              <Button data-testid="pay-charges-pay-button" onClick={pay} disabled={paying} className="w-full mt-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-12">
                {paying ? <Loader2 size={16} className="animate-spin" /> : "Pay Now by Card"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center font-ui">Secure checkout by Stripe · Visa, Mastercard, Google Pay & Apple Pay accepted</p>
            </>
          ) : (
            <p data-testid="pay-charges-settled" className="flex items-center gap-2 text-sm font-ui text-emerald-600 pt-2">
              <ShieldCheck size={16} /> All post-rental charges on this booking are settled.
            </p>
          )}
          <Link to={`/receipt/${b.id}`} className="block text-center text-xs font-ui text-muted-foreground hover:text-primary transition-colors duration-200">
            View full invoice →
          </Link>
        </div>
      )}
    </div>
  );
}
